import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import Dialog from 'shared/components/widgets/dialog';
import Autocomplete from 'shared/components/widgets/autocomplete';
import FileUploadingDialogContent from 'shared/components/dialog-content/file-uploading';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import UploadFile from 'shared/components/widgets/upload-file';

import { ROUTES } from 'constants/routes';
import RouteLink from 'shared/components/widgets/route-link';
import AutocompleteContainer from 'shared/components/widgets/autocomplete-container';
import InputContainer from 'shared/components/widgets/input-container';
import { classificationDropdownList } from 'constants/dropdown/external-delivery/classification';
import { departmentDropdownList } from 'constants/dropdown/external-delivery/department';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import { externalDeliveryFileRegisterService } from '@services/private/external-delivery/file-register.service';
import { is200xRange } from '@utils/utilities';
import { externalDeliveryFileUploadInitiateService } from '@services/private/external-delivery/file-upload/file-upload-initiate';
import { externalDeliveryFileUploadUrlAssignService } from '@services/private/external-delivery/file-upload/file-upload-url-assign';
import { externalDeliveryFileUploadInsertService } from '@services/private/external-delivery/file-upload/file-upload-insert';
import { externalDeliveryFileUploadCompleteService } from '@services/private/external-delivery/file-upload/file-upload-complete';
import { externalDeliveryFileUploadStopService } from '@services/private/external-delivery/file-upload/file-upload-stop';

const ExternalDeliveryFileRegister = () => {
  const { SUBJECT } = VALIDATION_MESSAGE.EXTERNAL_DELIVERY.REGISTER_FILE;

  const externalDeliveryFileRegisterSchema = yup.object({
    subject: yup
      .string()
      .required(SUBJECT.REQUIRED)
      .min(3, SUBJECT.MIN_LENGTH)
      .max(100, SUBJECT.MAX_LENGTH),
    shmLfsKind: yup.string().required('분류를 입력해 주세요'),
    shmDeptCd: yup.string().required('부서를 입력해 주세요'),
    file: yup.mixed().required('파일을 업로드 해주세요.'),
  });

  type ExternalDeliveryFileRegisterFormData = yup.InferType<
    typeof externalDeliveryFileRegisterSchema
  >;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<ExternalDeliveryFileRegisterFormData>({
    defaultValues: {
      file: undefined,
      subject: '',
      shmDeptCd: '',
      shmLfsKind: ''
    },
    resolver: yupResolver(externalDeliveryFileRegisterSchema),
  });

  const uploadedFile = watch('file');
  const navigate = useNavigate();
  const [retryUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [openUploadingDialog, setOpenUploadingDialog] = useState(false);
  const handleOpenUploading = () => setOpenUploadingDialog(true);
  const handleCloseUploading = () => setOpenUploadingDialog(false);
  const [shmSeq, setShmSeq] = useState('');
  const [shmfSeq, setShmfSeq] = useState('');
  const [uploadId, setUploadId] = useState('');

  const abortControllersRef = useRef<AbortController[]>([]);

  const resetUploadState = () => {
    setUploadProgress(0); // Reset progress to 0%
    // setUploadedFile(null); // Clear the uploaded file state
    setUploadId(''); // Clear the uploadId
  };

  const stopUploading = async (
    shmSeq: string,
    shmfSeq: string,
    uploadId: string,
  ) => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current = [];

    const { response, statusCode } =
      await externalDeliveryFileUploadStopService({
        shmSeq,
        shmfSeq,
        uploadId,
      });

    if (response?.code === '1' && is200xRange(statusCode)) {
    }
  };

  const registerFile = async (subject, shmLfsKind, shmDeptCd, file) => {
    resetUploadState();
    const controller = new AbortController();
    abortControllersRef.current.push(controller);

    const {
      response: uploadRegisterResponse,
      statusCode: uploadRegisterStatusCode,
      error: uploadRegisterError,
    } = await externalDeliveryFileRegisterService(
      {
        subject,
        shmLfsKind,
        shmDeptCd,
      },
      controller.signal,
    );
    if (uploadRegisterError || !is200xRange(uploadRegisterStatusCode)) {
      return;
    }

    if (
      uploadRegisterResponse?.code === '1' &&
      is200xRange(uploadRegisterStatusCode)
    ) {
      const controller2 = new AbortController();
      abortControllersRef.current.push(controller2);

      setShmSeq(uploadRegisterResponse?.result?.shmInfo?.shmSeq);
      const {
        response: uploadInitiateResponse,
        statusCode: uploadInitiateStatusCode,
        error: uploadInitiateError,
      } = await externalDeliveryFileUploadInitiateService(
        {
          shmSeq: uploadRegisterResponse?.result?.shmInfo?.shmSeq,
          fileName: file?.name,
          fileSize: file?.size,
        },
        controller2.signal,
      );
      if (uploadInitiateError || !is200xRange(uploadInitiateStatusCode)) {
        return;
      }

      if (
        uploadInitiateResponse?.code === '1' &&
        is200xRange(uploadInitiateStatusCode)
      ) {
        const { shmfSeq, partSize, uploadId } =
          uploadInitiateResponse?.result?.shmFileInfo;
        setUploadId(uploadId);
        const totalParts = Math.ceil(file?.size / partSize);
        const etags: {
          partNumber: number;
          etag: string;
        }[] = [];

        setShmfSeq(shmfSeq);

        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          const controller3 = new AbortController();
          abortControllersRef.current.push(controller3);

          setUploadProgress(Math.trunc((partNumber / totalParts) * 100));

          const {
            response: uploadUrlAssignResponse,
            statusCode: uploadUrlAssignStatusCode,
            error: uploadUrlAssignError,
          } = await externalDeliveryFileUploadUrlAssignService(
            {
              shmSeq: uploadRegisterResponse?.result?.shmInfo?.shmSeq,
              shmfSeq,
              uploadId,
              partNumber,
            },
            controller3.signal,
          );
          if (uploadUrlAssignError || !is200xRange(uploadUrlAssignStatusCode)) {
            stopUploading(shmSeq, shmfSeq, uploadId)
            handleCloseUploading();
            navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT);
          }
          if (
            uploadUrlAssignResponse?.code === '1' &&
            is200xRange(uploadUrlAssignStatusCode)
          ) {
            const start = (partNumber - 1) * partSize;
            const end = Math.min(start + partSize, file.size);
            const filePart = file.slice(start, end);
            const { presignedUrl } =
              uploadUrlAssignResponse?.result?.shmFileInfo;
            const uploadResponse = await fetch(presignedUrl, {
              method: 'PUT',
              body: filePart,
              headers: {
                'Content-Type': file.type,
              },
              signal: controller3.signal,
            });
            const etag = uploadResponse.headers.get('ETag');
            // @ts-ignore
            etags.push({ partNumber, etag });

            await externalDeliveryFileUploadInsertService(
              {
                shmSeq: uploadRegisterResponse?.result?.shmInfo?.shmSeq,
                shmfSeq: uploadInitiateResponse?.result?.shmFileInfo?.shmfSeq,
                partNumber,
                etag,
              },
              controller3.signal,
            );
          }
        }

        const {
          response: uploadCompleteResponse,
          statusCode: uploadCompleteStatusCode,
          error: uploadCompleteError,
        } = await externalDeliveryFileUploadCompleteService(
          {
            shmSeq: uploadRegisterResponse?.result?.shmInfo?.shmSeq,
            shmfSeq: uploadInitiateResponse?.result?.shmFileInfo?.shmfSeq,
            partArray: etags,
          },
          controller.signal,
        );
        if (uploadCompleteError || !is200xRange(uploadCompleteStatusCode)) {
          return;
        }

        if (
          uploadCompleteResponse?.code === '1' &&
          is200xRange(uploadCompleteStatusCode)
        ) {
          handleCloseUploading();
          navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT);
        }
      }
    }
  };

  const onSubmit = (data: any) => {
    const { subject, shmLfsKind, shmDeptCd, file } = data;
    registerFile(subject, shmLfsKind, shmDeptCd, file);
    handleOpenUploading();
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.EXTERNAL_DELIVERY_FILES.ROOT}>Home</RouteLink>
          <RouteLink to={ROUTES.EXTERNAL_DELIVERY_FILES.ROOT}>
            외부 공유용 대용량 파일
          </RouteLink>
          <RouteLink
            isActive
            to={ROUTES.EXTERNAL_DELIVERY_FILES.REGISTER_FILE.path}
          >
            {retryUpload
              ? '대용량 파일 업로드 재시도'
              : '대용량 등록 파일 수정'}
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          외부 공유용 대용량 파일
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={6}
          marginBottom="50px"
          position="relative"
        >
          {retryUpload && (
            <Button
              variant="outlined"
              sx={{
                width: '112px',
                position: 'absolute',
                right: 0,
                top: '-24px',
              }}
            >
              삭제하기
            </Button>
          )}
          {/* BASIC INFORMATION */}
          <FieldBox title="기본 정보">
            {/* UPLOAD TITLE */}
            <FieldBoxProperty
              name={retryUpload ? '업로드 제목' : '* 업로드 제목'}
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  'MDM 이용 가이드 문서입니다.'
                ) : (
                  <Controller
                    name="subject"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          <Input
                            {...field}
                            placeholder="업로드 제목을 입력해 주세요."
                            showErrorMsg={!!errors.subject}
                            errorMessage={
                              errors.subject && errors.subject.message
                            }
                            value={field.value}
                          />
                        </InputContainer>
                      );
                    }}
                  />
                )
              }
            />
            {/* CLASSIFICATION */}
            <FieldBoxProperty
              name={retryUpload ? '분류' : '* 분류'}
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  '제안서'
                ) : (
                  <AutocompleteContainer type="form">
                    <Controller
                      name="shmLfsKind"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            {...field}
                            showErrorMsg={!!errors.shmLfsKind?.message}
                            errorMessage={errors.shmLfsKind?.message}
                            placeholder="분류를 선택해 주세요."
                            options={classificationDropdownList}
                            defaultValue={{
                              label: '',
                              value: '',
                            }}
                            value={field?.value ?? ''}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            onChange={(_, newValue, reason) => {
                              field.onChange(newValue?.value);
                              if (reason === 'clear') {
                                field.onChange('');
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </AutocompleteContainer>
                )
              }
            />
            {/* DEPARTMENT */}
            <FieldBoxProperty
              name={retryUpload ? '부서' : '* 부서'}
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  'PD본부'
                ) : (
                  <AutocompleteContainer type="form">
                    <Controller
                      name="shmDeptCd"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            {...field}
                            placeholder="부서를 선택해 주세요."
                            showErrorMsg={!!errors.shmDeptCd?.message}
                            errorMessage={errors.shmDeptCd?.message}
                            options={departmentDropdownList}
                            defaultValue={{
                              label: '',
                              value: '',
                            }}
                            value={field?.value ?? ''}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            onChange={(_, newValue, reason) => {
                              field.onChange(newValue?.value);
                              if (reason === 'clear') {
                                field.onChange('');
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </AutocompleteContainer>
                )
              }
            />

            {/* UPLOAD STATUS */}

            {retryUpload && (
              <FieldBoxProperty
                name="업로드 상태"
                type="text"
                value="업로드 실패"
              />
            )}
          </FieldBox>

          {/* UPLOAD FILE */}
          <FieldBox title="업로드 파일">
            <FieldBoxProperty
              name="* 업로드 파일"
              value={
                <Box>
                  <UploadFile
                    uploadedFile={uploadedFile}
                    setUploadedFile={(file) => setValue('file', file)}
                  />
                  {!uploadedFile && errors.file && (
                    <Typography color={'var(--danger-500)'}>
                      {errors.file.message}
                    </Typography>
                  )}
                </Box>
              }
            />
          </FieldBox>
        </Box>

        <Box display="flex" gap="14px">
          {/* CANCEL */}
          <Button
            variant="outlined"
            color="secondary"
            type="button"
            onClick={() => navigate(ROUTES.EXTERNAL_DELIVERY_FILES.ROOT)}
          >
            취소
          </Button>
          {/* SAVE */}
          <Button
            variant="contained"
            color="primary"
            type={'submit'}
          // disabled={!watch('subject') || !watch('shmDeptCd') || !watch('file') || !watch('shmLfsKind')}
          >
            {retryUpload ? '파일 다시 업로드 하기' : '저장하기'}
          </Button>
        </Box>
      </Box>
      <Dialog
        isBackDropClickClose={false}
        open={openUploadingDialog}
        closeAction={handleCloseUploading}
        width={348}
        height={304}
        isCloseIcon={false}
      >
        <FileUploadingDialogContent
          closeAction={handleCloseUploading}
          progress={uploadProgress}
          stopAction={() => stopUploading(shmSeq, shmfSeq, uploadId)}
        />
      </Dialog>
    </Box>
  );
};

export default ExternalDeliveryFileRegister;
